import React from "react";
import "../../pages/Surveys/style.css";
import cogoToast from "cogo-toast";
import "survey-core/defaultV2.min.css";
import "survey-creator-core/survey-creator-core.min.css";
import { Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Form } from "reactstrap";
import { useState, useEffect, useRef, useMemo, useContext } from "react";
import { createPortal } from "react-dom";
import { FaArchive, FaClone } from "react-icons/fa";
// import { SurveyCreatorComponent, SurveyCreator } from "survey-creator-react";
import AppContext from "../../AppContext";

import { genericPost, listSurveys, UpdateSurveyStatus, cloneSurvey } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";
import { FaTimes, FaEdit, FaPlus, FaEye, FaSearch, FaSortUp, FaSortDown, FaSort, FaChartBar, FaEllipsisV, FaTrash } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import ability from '../../pages/config/defineAbility';
import { PERMS } from '../../pages/config/perms'

// Beautiful Confirmation Dialog Component
const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel", type = "warning" }) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return '⚠️';
      case 'success':
        return '✅';
      case 'info':
        return 'ℹ️';
      default:
        return '⚠️';
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case 'danger':
        return 'danger';
      case 'success':
        return 'success';
      case 'info':
        return 'info';
      default:
        return 'warning';
    }
  };

  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      backdropFilter: 'blur(5px)'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '0',
        maxWidth: '400px',
        width: '90%',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        animation: 'dialogSlideIn 0.3s ease-out'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            fontSize: '18px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: type === 'danger' ? '#fef2f2' : type === 'success' ? '#f0fdf4' : '#fffbeb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {getIcon()}
          </div>
          <div>
            <h3 style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: '600',
              color: '#111827'
            }}>
              {title}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px 24px',
          color: '#6b7280',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          {message}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 20px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button
            color="light"
            onClick={onClose}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {cancelText}
          </Button>
          <Button
            color={getButtonColor()}
            onClick={onConfirm}
            style={{
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: 'none'
            }}
          >
            {confirmText}
          </Button>
        </div>
      </div>

      <style jsx>{`
        @keyframes dialogSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>,
    document.body
  );
};

const Surveys = () => {
  const history = useHistory();
  const [is_processing, setProcessing] = useState(false);

  const [rowData, setRowData] = useState([]);
  
  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const global = useContext(AppContext);

  // Helper function to show confirmation dialog
  const showConfirmDialog = (title, message, type = 'warning', confirmText = 'Confirm', cancelText = 'Cancel') => {
    return new Promise((resolve) => {
      setConfirmDialog({
        isOpen: true,
        title,
        message,
        type,
        confirmText,
        cancelText,
        onConfirm: () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        }
      });
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  // Dropdown functions
  const toggleDropdown = (surveyId, event) => {
    if (openDropdown === surveyId) {
      setOpenDropdown(null);
    } else {
      // Calculate button position for portal rendering
      const buttonRect = event.target.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom + window.scrollY,
        left: buttonRect.left + window.scrollX - 150 // Position to the left of the button
      });
      setOpenDropdown(surveyId);
    }
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  const handleActionClick = (action, survey) => {
    closeDropdown();
    // Handle the specific action
    switch (action) {
      case 'view':
        localStorage.setItem("current_survey_id", survey.survey_id);
        get_survey_details(survey.survey_id, "/ebms/surveys/submissions");
        break;
      case 'edit':
        localStorage.setItem("current_survey_id", survey.survey_id);
        localStorage.setItem("current_survey_name", survey.name);
        get_survey_details(survey.survey_id, "/ebms/surveys/create");
        break;
      case 'delete':
        handleDeleteSurvey(survey);
        break;
      default:
        break;
    }
  };

  const handleDeleteSurvey = async (survey) => {
    if (survey.status != 10002) {
      const confirmed = await showConfirmDialog(
        "Delete Survey",
        "Are you sure you want to delete this survey? ["+survey.name+"] This action cannot be undone and will permanently remove the survey.",
        "danger",
        "Delete",
        "Cancel"
      );
      if (confirmed) {
        UpdateSurveyStatus({
          survey_id: survey.survey_id,
          status: 10002,
        })
          .then((res) => {
            var response = res.data;
            if (response.status == SUCCESS_RESULT) {
              cogoToast.success(res.data.message, {
                hideAfter: 5,
              });
              list_surveys();
            } else {
              cogoToast.error(res.data.message, {
                hideAfter: 5,
              });
            }
          })
          .catch((e) => {
            global.handle_api_error(e);
          });
      }
    }
  };

  // Clone survey modal state
  const [cloneModal, setCloneModal] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  const [cloneFormData, setCloneFormData] = useState({
    name: "",
    description: "",
    location: "",
    start_date: "",
    end_date: ""
  });
  const [isCloning, setIsCloning] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Table search and pagination state
  const [tableSearch, setTableSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Filter, sort and paginate data
  const filteredData = useMemo(() => {
    let data = rowData;
    
    // Apply search filter
    if (tableSearch) {
      data = data.filter(survey => 
        survey.name?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        survey.description?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        survey.location?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        survey.country_name?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        survey.created_by_name?.toLowerCase().includes(tableSearch.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField) {
      data = [...data].sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        // Handle null/undefined values
        if (aValue == null) aValue = "";
        if (bValue == null) bValue = "";
        
        // Convert to string for comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
        
        if (sortDirection === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }
    
    return data;
  }, [rowData, tableSearch, sortField, sortDirection]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);


  useEffect(() => {
    list_surveys();

    return () => { };
  }, []);
  const get_survey_details=(survey_id,route)=>{
   
  
    genericPost(ENDPOINTS.SURVEY_DETAILS, { "survey_id": survey_id })
    .then((res) => {
     
      if (res.status == 200) {
        if (res.data.status == SUCCESS_RESULT) {
        
        console.clear();
         
          let template= res.data.data[0];
          template.survey_id=survey_id;
          console.log("template",template)
       
          history.push({
            pathname: route,
            state: { _survey: template},
          });
        
    
    
        } else {
          
          cogoToast.error(res.data.message, {});

        }
      } else
      {
        alert("Request was not successful")
      }
    })
    .catch((err) => {
      // setIsLoading(false);
      console.log(err);
    });
      }
  const list_surveys = () => {
    setProcessing(true);

    listSurveys({})
      .then((res) => {
        setProcessing(false);
        //gridRef.current.api.showLoadingOverlay();
        if (res.status == 200) {
          var response = res.data;
          console.log(response);
          if (response.status == SUCCESS_RESULT) {
            setRowData(response.data);
          } else {
            cogoToast.error(res.data.message, {
              hideAfter: 5,
            });
          }
        } else {
          {
            cogoToast.error(res.message, {
              hideAfter: 5,
            });
          }
          cogoToast.error(res.message, {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setProcessing(false);
        console.log(err);
        cogoToast.error(err, {
          hideAfter: 5,
        });

        cogoToast.error("Something went wrong, please Try again.", {
          hideAfter: 5,
        });
      });
  };

  // Clone survey functions
  const toggleCloneModal = () => {
    setCloneModal(!cloneModal);
    if (!cloneModal) {
      // Reset form when opening modal
      setSelectedSurveyId("");
      setCloneFormData({
        name: "",
        description: "",
        location: "",
        start_date: "",
        end_date: ""
      });
      setFormErrors({});
      setSearchTerm("");
      setShowDropdown(false);
    }
  };

  const handleCloneFormChange = (e) => {
    const { name, value } = e.target;
    setCloneFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Searchable dropdown functions
  const handleSurveySearch = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  const handleSurveySelect = (survey) => {
    setSelectedSurveyId(survey.survey_id);
    setSearchTerm(survey.name);
    setShowDropdown(false);
    
    // Clear error for survey selection
    if (formErrors.surveySelect) {
      setFormErrors(prev => ({
        ...prev,
        surveySelect: ""
      }));
    }
  };

  const filteredSurveys = rowData.filter(survey =>
    survey.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Table functions
  const handleTableSearch = (e) => {
    setTableSearch(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.survey-dropdown-container')) {
        setShowDropdown(false);
      }
      if (openDropdown && !event.target.closest('.dropdown-menu') && !event.target.closest('button[title="Actions"]')) {
        closeDropdown();
      }
    };

    if (showDropdown || openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, openDropdown]);

  const handleCloneSurvey = () => {
    const errors = {};

    if (!selectedSurveyId) {
      errors.surveySelect = "Please select a survey to clone";
    }

    if (!cloneFormData.name.trim()) {
      errors.name = "Please enter a survey name";
    }

    if (!cloneFormData.location.trim()) {
      errors.location = "Please enter a location";
    }

    if (!cloneFormData.start_date) {
      errors.start_date = "Please select a start date";
    }

    if (!cloneFormData.end_date) {
      errors.end_date = "Please select an end date";
    }

    // Check if end date is after start date
    if (cloneFormData.start_date && cloneFormData.end_date) {
      if (new Date(cloneFormData.end_date) <= new Date(cloneFormData.start_date)) {
        errors.end_date = "End date must be after start date";
      }
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsCloning(true);

    const cloneData = {
      survey_id: selectedSurveyId,
      name: cloneFormData.name,
      description: cloneFormData.description,
      location: cloneFormData.location,
      start_date: cloneFormData.start_date,
      end_date: cloneFormData.end_date,
      mode: 10001
    };

    cloneSurvey(cloneData)
      .then((res) => {
        setIsCloning(false);
        if (res.status === 200) {
          if (res.data.status === SUCCESS_RESULT) {
            cogoToast.success("Survey cloned successfully!", { hideAfter: 5 });
            toggleCloneModal();
            list_surveys(); // Refresh the survey list
          } else {
            cogoToast.error(res.data.message, { hideAfter: 5 });
          }
        } else {
          cogoToast.error("Request was not successful", { hideAfter: 5 });
        }
      })
      .catch((err) => {
        setIsCloning(false);
        console.log(err);
        cogoToast.error("Something went wrong, please try again.", { hideAfter: 5 });
      });
  };
  return (
    <div>
      <style>
        {`
          .clone-survey-modal .modal-dialog {
            z-index: 10000 !important;
          }
          .clone-survey-modal .modal-content {
            z-index: 10001 !important;
            position: relative;
          }
          .clone-survey-modal .modal-backdrop {
            z-index: 9999 !important;
          }
          
          .sortable-header:hover {
            background-color: rgba(255, 255, 255, 0.1) !important;
            transition: background-color 0.2s ease;
          }
          
          .table-row-hover:hover {
            background-color: rgba(0, 123, 255, 0.05) !important;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
          }
          
          .badge {
            font-size: 0.75em;
            padding: 0.05em 0.05em;
          }
          
          .btn-group-sm .btn {
            padding: 0.25rem 0.5rem;
            font-size: 0.875rem;
          }
          
          .text-truncate {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          
          .cursor-pointer {
            cursor: pointer;
          }
          
          .pagination-sm .page-link {
            padding: 0.25rem 0.5rem;
            font-size: 0.875rem;
          }
          
          .position-relative {
            overflow: visible !important;
          }
          
          .actions-dropdown {
            position: absolute !important;
            z-index: 9999 !important;
            background: white !important;
            border: 1px solid #dee2e6 !important;
            border-radius: 0.375rem !important;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
          }
          
          .table-responsive {
            overflow: visible !important;
          }
          
          .table-responsive .table {
            overflow: visible !important;
          }
        `}
      </style>
      <div style={{ marginBottom: "15px" }}>
       {ability.can(PERMS.CREATE, PERMS.CREATE_SURVEYS) && (
      <Button
        className="btn btn-info"
        onClick={() => {
          localStorage.removeItem("current_survey_id");
          localStorage.removeItem("current_survey_name");
          history.push({
            pathname: "/ebms/surveys/create",
            state: {},
          });
        }}
            style={{ marginRight: "10px" }}
      >
        + CREATE NEW
          </Button>
        )}
        
        {ability.can(PERMS.CREATE, PERMS.CREATE_SURVEYS) && (
          <Button
            className="" style={{ backgroundColor: '#ccff99', borderColor: '#28a745' }}
            onClick={toggleCloneModal}
          >
            <FaClone style={{ color: '#FFF' }} /> &nbsp; CLONE EXISTING SURVEY
          </Button>
        )}
        
      
      </div>
      {/* {JSON.stringify(rowData)} */}
      {is_processing && (
        <center>
          <div className="loadr">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>{" "}
        </center>
      )}
      {/* Enhanced Search Bar */}
      <div className="mb-4">
        <div className="row">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text bg-primary text-white">
                <FaSearch />
              </span>
              <Input
                type="text"
                className="form-control form-control-lg"
                placeholder="Search surveys by name, description, location, country, or owner..."
                value={tableSearch}
                onChange={handleTableSearch}
                style={{ borderLeft: 'none' }}
              />
            </div>
          </div>
          <div className="col-md-6 d-flex align-items-center">
            <div className="text-muted">
              <small>
                {filteredData.length} survey{filteredData.length !== 1 ? 's' : ''} found
                {tableSearch && ` matching "${tableSearch}"`}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Data Table */}
      <div className="table-responsive shadow-sm">
        <table className="table table-striped table-hover mb-0">
          <thead className="table-dark">
            <tr>
              <th style={{ width: '50px' }}>#</th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('name')}
                className="sortable-header"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Name</span>
                  <span>
                    {sortField === 'name' ? (
                      sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort style={{ opacity: 0.3 }} />
                    )}
                  </span>
                </div>
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('description')}
                className="sortable-header"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Description</span>
                  <span>
                    {sortField === 'description' ? (
                      sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort style={{ opacity: 0.3 }} />
                    )}
                  </span>
                </div>
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('location')}
                className="sortable-header"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Location</span>
                  <span>
                    {sortField === 'location' ? (
                      sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort style={{ opacity: 0.3 }} />
                    )}
                  </span>
                </div>
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('startdate')}
                className="sortable-header"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Start Date</span>
                  <span>
                    {sortField === 'startdate' ? (
                      sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort style={{ opacity: 0.3 }} />
                    )}
                  </span>
                </div>
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('enddate')}
                className="sortable-header"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>End Date</span>
                  <span>
                    {sortField === 'enddate' ? (
                      sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort style={{ opacity: 0.3 }} />
                    )}
                  </span>
                </div>
              </th>
           
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('created_by_name')}
                className="sortable-header"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Owner</span>
                  <span>
                    {sortField === 'created_by_name' ? (
                      sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort style={{ opacity: 0.3 }} />
                    )}
                  </span>
                </div>
              </th>
              {/* <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('submissions_count_count')}
                className="sortable-header"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span>Live Count</span>
                  <span>
                    {sortField === 'submissions_count_count' ? (
                      sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort style={{ opacity: 0.3 }} />
                    )}
                  </span>
                </div>
              </th> */}
              <th style={{ width: '100px' }}>Status</th>
              <th style={{ width: '200px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((survey, index) => (
              <tr 
                key={survey.survey_id}
                onDoubleClick={() => {
                  localStorage.setItem("current_survey_id", survey.survey_id);
                  get_survey_details(survey.survey_id, "/ebms/surveys/submissions");
                }}
                style={{ cursor: 'pointer' }}
                className="table-row-hover"
              >
                <td className="text-muted fw-bold">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td>
                  <div className="fw-semibold text-primary">{survey.name}</div>
                </td>
                <td>
                  <div className="text-truncate" style={{ maxWidth: '200px' }} title={survey.description}>
                    {survey.description || <span className="text-muted">No description</span>}
                  </div>
                </td>
                <td>
               
                   
                    <span className="badge bg-light text-dark">
                    <div className="text-truncate" style={{ maxWidth: '200px' }} title={survey.description}>
                      {survey.location}
                  </div>
                  </span>
                </td>
                <td>
                  <small className="text-muted">{survey.startdate}</small>
                </td>
                <td>
                  <small className="text-muted">{survey.enddate}</small>
                </td>
              
                <td>
                  <small className="text-muted">{survey.created_by_name}</small>
                </td>
                {/* <td className="text-center">
                  <span className="badge bg-success" style={{fontSize: '0.75em', padding: '1.35em 1.65em' , color: 'white'}}>{survey.submissions_count_count || 0}</span>
                </td> */}
                <td>
                  <span
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (survey.status == 10000) {
                        const confirmed = await showConfirmDialog(
                          "Deploy Survey",
                          "Are you sure you want to deploy this survey? ["+survey.name+"] This will make it live and available for responses.",
                          "success",
                          "Deploy",
                          "Cancel"
                        );
                        if (confirmed) {
                          UpdateSurveyStatus({
                            survey_id: survey.survey_id,
                            status: 10001,
                          })
                            .then((res) => {
                              var response = res.data;
                              if (response.status == SUCCESS_RESULT) {
                                cogoToast.success(res.data.message, {
                                  hideAfter: 5,
                                });
                                list_surveys();
                              } else {
                                cogoToast.error(res.data.message, {
                                  hideAfter: 5,
                                });
                              }
                            })
                            .catch((e) => {
                              global.handle_api_error(e);
                            });
                        }
                      }
                      if (survey.status == 10001) {
                        const confirmed = await showConfirmDialog(
                          "Move to Draft ["+survey.name+"]",
                          "Are you sure you want to move this survey back to draft status? This will make it unavailable for new responses.",
                          "warning",
                          "Move to Draft",
                          "Cancel"
                        );
                        if (confirmed) {
                          UpdateSurveyStatus({
                            survey_id: survey.survey_id,
                            status: 10000,
                          })
                            .then((res) => {
                              var response = res.data;
                              if (response.status == SUCCESS_RESULT) {
                                cogoToast.success(res.data.message, {
                                  hideAfter: 5,
                                });
                                list_surveys();
                              } else {
                                cogoToast.error(res.data.message, {
                                  hideAfter: 5,
                                });
                              }
                            })
                            .catch((e) => {
                              global.handle_api_error(e);
                            });
                        }
                      }
                    }}
                    className={`badge ${survey.status == 10000 ? 'bg-warning text-dark' : 'bg-success'} cursor-pointer`}
                    style={{fontSize: '0.55em', padding: '1.0em 1.0em' , color: 'white'}}
                  >
                    {survey.status == 10000 && "DRAFT"}
                    {survey.status == 10001 && "DEPLOYED"}
                  </span>
                </td>
                <td>
                  <div className="position-relative">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(survey.survey_id, e);
                      }}
                      style={{fontSize: '0.75em', padding: '0.5em 0.75em'}}
                      title="Actions"
                    >
                      <FaEllipsisV style={{fontSize: '1em'}} />
                    </button>
                    
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Enhanced Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="text-muted">
            <small>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} surveys
            </small>
          </div>
          
          <nav aria-label="Survey pagination">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <span aria-hidden="true">&laquo;</span>
                </button>
              </li>
              
              {/* Show page numbers with smart pagination */}
              {(() => {
                const pages = [];
                const maxVisiblePages = 5;
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }
                
                if (startPage > 1) {
                  pages.push(
                    <li key={1} className="page-item">
                      <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                    </li>
                  );
                  if (startPage > 2) {
                    pages.push(<li key="ellipsis1" className="page-item disabled"><span className="page-link">...</span></li>);
                  }
                }
                
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
                    </li>
                  );
                }
                
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(<li key="ellipsis2" className="page-item disabled"><span className="page-link">...</span></li>);
                  }
                  pages.push(
                    <li key={totalPages} className="page-item">
                      <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                    </li>
                  );
                }
                
                return pages;
              })()}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <span aria-hidden="true">&raquo;</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* No results message */}
      {filteredData.length === 0 && (
        <div className="text-center py-5">
          <div className="text-muted">
            <FaSearch size={48} className="mb-3" style={{ opacity: 0.3 }} />
            <h6 style={{fontSize: '0.9rem'}}>No surveys found</h6>
            <p>
              {tableSearch ? `No surveys match your search "${tableSearch}"` : 'No surveys available'}
            </p>
            {tableSearch && (
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={() => setTableSearch('')}
              >
                Clear search
              </button>
            )}
          </div>
        </div>
      )}

      {/* Clone Survey Modal */}
      {cloneModal && createPortal(
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block', 
            zIndex: 10000,
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              toggleCloneModal();
            }
          }}
        >
          <div className="modal-backdrop fade show" style={{ zIndex: 9999 }}></div>
          <div 
            className="modal-dialog modal-lg" 
            style={{ 
              zIndex: 10001,
              position: 'relative',
              margin: '1.75rem auto',
              maxWidth: '800px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{ zIndex: 10002 }}>
              <div className="modal-header">
                <h6 className="modal-title" style={{fontSize: '1rem'}}>Clone Survey</h6>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={toggleCloneModal}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <Form>
                  <FormGroup>
                    <Label for="surveySelect">Select Survey to Clone *</Label>
                    <div className="survey-dropdown-container" style={{ position: 'relative' }}>
                      <Input
                        type="text"
                        name="surveySelect"
                        id="surveySelect"
                        value={searchTerm}
                        onChange={handleSurveySearch}
                        onFocus={() => setShowDropdown(true)}
                        placeholder="Search and select a survey..."
                        invalid={!!formErrors.surveySelect}
                        required
                        autoComplete="off"
                      />
                      {showDropdown && (
                        <div 
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ced4da',
                            borderTop: 'none',
                            borderRadius: '0 0 0.375rem 0.375rem',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
                          }}
                        >
                          {filteredSurveys.length > 0 ? (
                            filteredSurveys.map((survey) => (
                              <div
                                key={survey.survey_id}
                                onClick={() => handleSurveySelect(survey)}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f8f9fa',
                                  fontSize: '0.875rem'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'white';
                                }}
                              >
                                {survey.name}
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: '0.5rem 0.75rem', color: '#6c757d', fontSize: '0.875rem' }}>
                              No surveys found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {formErrors.surveySelect && (
                      <div className="invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {formErrors.surveySelect}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label for="name">New Survey Name *</Label>
                    <Input
                      type="text"
                      name="name"
                      id="name"
                      value={cloneFormData.name}
                      onChange={handleCloneFormChange}
                      placeholder="Enter new survey name"
                      invalid={!!formErrors.name}
                      required
                    />
                    {formErrors.name && (
                      <div className="invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {formErrors.name}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label for="description">Description</Label>
                    <Input
                      type="textarea"
                      name="description"
                      id="description"
                      value={cloneFormData.description}
                      onChange={handleCloneFormChange}
                      placeholder="Enter survey description"
                      rows="3"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label for="location">Location *</Label>
                    <Input
                      type="text"
                      name="location"
                      id="location"
                      value={cloneFormData.location}
                      onChange={handleCloneFormChange}
                      placeholder="Enter survey location"
                      invalid={!!formErrors.location}
                      required
                    />
                    {formErrors.location && (
                      <div className="invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {formErrors.location}
                      </div>
                    )}
                  </FormGroup>

                  <div style={{ display: "flex", gap: "15px" }}>
                    <FormGroup style={{ flex: 1 }}>
                      <Label for="start_date">Start Date *</Label>
                      <Input
                        type="date"
                        name="start_date"
                        id="start_date"
                        value={cloneFormData.start_date}
                        onChange={handleCloneFormChange}
                        invalid={!!formErrors.start_date}
                        required
                      />
                      {formErrors.start_date && (
                        <div className="invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {formErrors.start_date}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup style={{ flex: 1 }}>
                      <Label for="end_date">End Date *</Label>
                      <Input
                        type="date"
                        name="end_date"
                        id="end_date"
                        value={cloneFormData.end_date}
                        onChange={handleCloneFormChange}
                        invalid={!!formErrors.end_date}
                        required
                      />
                      {formErrors.end_date && (
                        <div className="invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {formErrors.end_date}
                        </div>
                      )}
                    </FormGroup>
                  </div>
                </Form>
              </div>
              <div className="modal-footer">
                <Button color="secondary" onClick={toggleCloneModal} disabled={isCloning}>
                  Cancel
                </Button>
                <Button 
                  color="primary" 
                  onClick={handleCloneSurvey}
                  disabled={isCloning}
                >
                  {isCloning ? "Cloning..." : "Clone Survey"}
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Beautiful Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={confirmDialog.isOpen}
          onClose={closeConfirmDialog}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          type={confirmDialog.type}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />

        {/* Portal-rendered Actions Dropdown */}
        {openDropdown && createPortal(
          <div 
            className="dropdown-menu show"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 9999,
              minWidth: '150px',
              backgroundColor: 'white',
              border: '1px solid #dee2e6',
              borderRadius: '0.375rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={(e) => {
                e.stopPropagation();
                const survey = rowData.find(s => s.survey_id === openDropdown);
                if (survey) handleActionClick('view', survey);
              }}
              style={{fontSize: '0.85em', padding: '0.5rem 1rem'}}
            >
              <FaEye className="me-2" style={{fontSize: '0.9em', color: '#007bff'}} />
              View Submissions
            </button>

<button    className="dropdown-item d-flex align-items-center"
                 onClick={async (e) => {
                  var survey = rowData.find(s => s.survey_id === openDropdown);
                  e.stopPropagation();
                  if (survey.status == 10000) {
                    const confirmed = await showConfirmDialog(
                      "Deploy Survey",
                      "Are you sure you want to deploy this survey? ["+survey.name+"] This will make it live and available for responses.",
                      "success",
                      "Deploy",
                      "Cancel"
                    );
                    if (confirmed) {
                      UpdateSurveyStatus({
                        survey_id: rowData.find(s => s.survey_id === openDropdown).survey_id,
                        status: 10001,
                      })
                        .then((res) => {
                          var response = res.data;
                          if (response.status == SUCCESS_RESULT) {
                            cogoToast.success(res.data.message, {
                              hideAfter: 5,
                            });
                            list_surveys();
                          } else {
                            cogoToast.error(res.data.message, {
                              hideAfter: 5,
                            });
                          }
                        })
                        .catch((e) => {
                          global.handle_api_error(e);
                        });
                    }
                  }
                  if (survey.status == 10001) {
                    const confirmed = await showConfirmDialog(
                      "Move to Draft ["+survey.name+"]",
                      "Are you sure you want to move this survey back to draft status? This will make it unavailable for new responses.",
                      "warning",
                      "Move to Draft",
                      "Cancel"
                    );
                    if (confirmed) {
                      UpdateSurveyStatus({
                        survey_id: survey.survey_id,
                        status: 10000,
                      })
                        .then((res) => {
                          var response = res.data;
                          if (response.status == SUCCESS_RESULT) {
                            cogoToast.success(res.data.message, {
                              hideAfter: 5,
                            });
                            list_surveys();
                          } else {
                            cogoToast.error(res.data.message, {
                              hideAfter: 5,
                            });
                          }
                        })
                        .catch((e) => {
                          global.handle_api_error(e);
                        });
                    }
                  }
                }}
                style={{fontSize: '0.85em', padding: '0.5rem 1rem'}}>
 
                       <FaArchive className="me-2" style={{fontSize: '0.9em', color: '#6c757d'}} />
                    {rowData.find(s => s.survey_id === openDropdown).status == 10000 && "Deploy Survey"}
                    {rowData.find(s => s.survey_id === openDropdown).status == 10001 && "Move to Draft"}
               {/* className={`badge ${rowData.find(s => s.survey_id === openDropdown).status == 10000 ? 'bg-warning text-dark' : 'bg-success'} cursor-pointer`} */}


</button>




            
            {ability.can(PERMS.UPDATE, PERMS.UPDATE_SURVEYS) && (
              <button
                className="dropdown-item d-flex align-items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  const survey = rowData.find(s => s.survey_id === openDropdown);
                  if (survey) handleActionClick('edit', survey);
                }}
                style={{fontSize: '0.85em', padding: '0.5rem 1rem'}}
              >
                <FaEdit className="me-2" style={{fontSize: '0.9em', color: '#6c757d'}} />
                Edit Survey
              </button>
            )}
            
       
            
            <button
              className="dropdown-item d-flex align-items-center text-danger"
              onClick={(e) => {
                e.stopPropagation();
                const survey = rowData.find(s => s.survey_id === openDropdown);
                if (survey) handleActionClick('delete', survey);
              }}
              style={{fontSize: '0.85em', padding: '0.5rem 1rem'}}
              disabled={rowData.find(s => s.survey_id === openDropdown)?.status == 10002}
            >
              <FaTrash className="me-2" style={{fontSize: '0.9em'}} />
              Delete Survey
            </button>
          </div>,
          document.body
        )}
      </div>
    );
  };

export const Loader = () => {
  return <div>Loading please wait...</div>;
};

export default Surveys;
